/**
 * The router (no router plugin or codegen, spec §6.9). The tree lives in `routeTree.ts`, the
 * layout routes in `layout-routes.tsx`.
 */
import type { QueryClient } from '@tanstack/react-query';
import { createRouter, type RouterHistory } from '@tanstack/react-router';
import { BASEPATH, type RouterContext } from './layout-routes';
import { NotFoundPage } from './pages/status-pages';
import { RouteError } from './route-guards';
import { routeTree } from './routeTree';

export { BASEPATH, safeRedirect, type RouterContext } from './layout-routes';

export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    history,
    basepath: BASEPATH,
    // The real auth value is supplied by <RouterProvider context> (see providers.tsx).
    context: { queryClient, auth: undefined as unknown as RouterContext['auth'] },
    defaultPreload: 'intent',
    // Route loaders read through TanStack Query, which owns caching; the router shouldn't cache too.
    defaultPreloadStaleTime: 0,
    defaultNotFoundComponent: NotFoundPage,
    defaultErrorComponent: RouteError,
    scrollRestoration: true,
    // Scroll positions are kept per page, not per URL: a change that only touches the search (a
    // chart's metric tab, a filter, a sort, a detail tab) keeps the scroll where it was, instead of
    // jumping back to the top.
    getScrollRestorationKey: (location) => location.pathname,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
